// ===========================================
// Enrollment Item Component
// ===========================================

import { Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  Delete as DeleteIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { StudentEnrollment } from '../../../api/users.api';
import { formatDayOfWeek, formatTime } from './students.helpers';

interface Props {
  enrollment: StudentEnrollment;
  onRemove?: () => void;
  isLoading?: boolean;
  showRemoveButton?: boolean;
}

/**
 * Single enrollment item display with optional remove button
 */
export default function EnrollmentItem({
  enrollment,
  onRemove,
  isLoading = false,
  showRemoveButton = true,
}: Props) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight="medium">
            {enrollment.lesson.name}
          </Typography>
          {enrollment.lesson.term && (
            <Chip
              label={enrollment.lesson.term.name}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {enrollment.lesson.instrument && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MusicNoteIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">
                {enrollment.lesson.instrument}
              </Typography>
            </Box>
          )}
          {enrollment.lesson.teacher && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {enrollment.lesson.teacher}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {enrollment.lesson.dayOfWeek == null && !enrollment.lesson.startTime && !enrollment.lesson.endTime
                ? 'Schedule TBD'
                : `${formatDayOfWeek(enrollment.lesson.dayOfWeek)} ${formatTime(enrollment.lesson.startTime)} - ${formatTime(enrollment.lesson.endTime)}`}
            </Typography>
          </Box>
          {enrollment.lesson.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {enrollment.lesson.location}
                {enrollment.lesson.room && ` - ${enrollment.lesson.room}`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {showRemoveButton && onRemove && (
        <Tooltip title="Remove from class">
          <IconButton
            size="small"
            color="error"
            onClick={onRemove}
            disabled={isLoading}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}
